<template>
  <div class="auth-form">
    <p class="auth-lead mb-3">Stávající a nové heslo</p>
    <argon-alert 
      v-for="msg in store.state.alert.message" :key="msg"
      v-show="store.state.alert.type == 'danger'"
      class="text-sm text-center mb-3" 
      icon="fa-solid fa-triangle-exclamation"
      :color="store.state.alert.type">
      {{ msg }}
    </argon-alert>
    <form @submit.prevent="changePassword">
        <div class="mb-3">
          <input
            ref="firstInput"
            class="form-control form-control-lg"
            type="password"
            v-model="credentials.currentPassword"
            placeholder="Aktuální heslo"
            name="currentPassword"
            autocomplete="off" 
          />
          <span v-if="v$.currentPassword.$error" class="text-danger text-sm">
            {{ v$.currentPassword.$errors[0].$message }}
          </span>
        </div>
        <div class="mb-3">
          <input
            class="form-control form-control-lg"
            type="password"
            v-model="credentials.newPassword"
            placeholder="Nové heslo"
            name="newPassword"
            autocomplete="new-password"
            @input="v$.newPassword.$touch"
            @blur="v$.newPassword.$touch" 
          />
          <span v-if="v$.newPassword.$error" class="text-danger text-sm">
            {{ v$.newPassword.$errors[0].$message }}
          </span>
        </div>
        <div class="mb-3">
          <input 
            class="form-control form-control-lg" 
            type="password"  
            v-model="credentials.confirmPassword" 
            placeholder="Nové heslo (pro kontrolu)" 
            name="confirmPassword"
            autocomplete="off"
            @input="v$.confirmPassword.$touch"
            @blur="v$.confirmPassword.$touch" 
          />
          <span v-if="v$.confirmPassword.$error" class="text-danger text-sm">
            {{ v$.confirmPassword.$errors[0].$message }}
          </span>
        </div>
        <argon-button
          class="mt-2"
          type="submit" 
          color="primary"
          size="lg"
          :disabled="isButtonLoading"
          full-width>
          <span v-if="isButtonLoading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
          Změnit heslo
        </argon-button>
      </form>
  </div>
</template>

<script setup>
import { reactive, computed, inject, ref, onMounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers, sameAs, minLength } from '@vuelidate/validators';
import authService from '@/services/authService';
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonAlert from "@/components/ArgonAlert.vue";

const store = useStore();
const swal = inject('$swal');
const firstInput = ref(null);

const initialState = {
  currentPassword: null,
  newPassword: null,
  confirmPassword: null,
};
const credentials = reactive({ ...initialState });
const isButtonLoading = computed(() => store.getters['loadingModule/isButtonLoading']);

onMounted(() => {
  nextTick(() => firstInput.value?.focus());
});

const rules = computed(() => {
  return {
    currentPassword: { 
      required: helpers.withMessage('Zadej své aktuální heslo', required), 
      minLength: helpers.withMessage('Heslo musí obsahovat alespoň 6 znaků', minLength(6)),
      containsPasswordRequirement: helpers.withMessage(
        () => 'Heslo musí obsahovat kombinaci velkých písmen, malých písmen a číslice', 
        (value) => /(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])/.test(value)),
    },
    newPassword: { 
      required: helpers.withMessage('Zadej své nové heslo.', required), 
      minLength: helpers.withMessage('Heslo musí obsahovat alespoň 6 znaků', minLength(6)),
      containsPasswordRequirement: helpers.withMessage(
        () => 'Heslo musí obsahovat kombinaci velkých písmen, malých písmen a číslice', 
        (value) => /(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])/.test(value)
      ),
    },
    confirmPassword: { sameAs: helpers.withMessage('Hesla se musí shodovat!', sameAs(credentials.newPassword)) }
  }
})
// initialize vuelidate
const v$ = useVuelidate(rules, credentials);

const resetForm = () => {
  v$.value.$reset();
  Object.assign(credentials, initialState);
}

const changePassword = async () => {
  const isFormCorrect = await v$.value.$validate();

  if (!isFormCorrect) return;

  try {
    store.dispatch('loadingModule/setButtonLoading', true);
    const response = await authService.UserPasswordChange(store.state.user.UserID, store.getters.isLoggedIn, credentials);

    if (response.Result) {
      store.dispatch('toggleAuthModal');
      swal.fire({
        position: 'center',
        icon: 'success',
        html: `<h5>${response.InfoMessage[0]}</h5>`,
        showConfirmButton: false,
        timer: 3000,
      })
    } else {
      store.dispatch('alertError', response.ErrorMessage);
      resetForm();
    }
  } catch (error) {
    console.error(error);
  } finally {
    store.dispatch('loadingModule/setButtonLoading', false);
  }
};

</script>

<style scoped>
.auth-lead {
  margin: 0;
  font-size: 0.88rem;
  font-weight: 600;
  color: #64748B;
}
</style>
